import { injectToolCSS } from "../utils/cssGenerator";
import { t } from "../i18n/translate";
import { IMAGE_SELECTORS } from "../enum/Selectors";
import isMobile from "../utils/isMobile";

export const imageTooltipsConfig = {
    id: 'image-tooltips',
    enable: false,
    css: `
        .asw-floating-tooltip {
            position: fixed;
            pointer-events: none;
            background: #000;
            backdrop-filter: blur(10px);
            color: #fff;
            padding: 10px 18px;
            border-radius: 14px;
            font-size: 14px;
            font-weight: 500;
            font-family: inherit;
            z-index: 1000002;
            box-shadow: 0 10px 30px #fff;
            border: 1px solid #fff;
            max-width: 300px;
            line-height: 1.4;
            opacity: 0;
            transition: opacity 0.2s ease;
            display: none;
            box-sizing: border-box;
            word-wrap: break-word;
        }
        .asw-floating-tooltip.asw-visible {
            display: block;
            opacity: 1;
        }
    `
};

let tooltipEl: HTMLDivElement | null = null;
let activeListeners: Map<HTMLElement, { move: any, leave: any, enter: any }> = new Map();

export default function imageTooltips(enable = false) {
    if (enable && isMobile()) {
        alert(t("Image Tooltips are designed for mouse-based navigation and are not supported on mobile devices."));
        return;
    }

    const images = Array.from(document.querySelectorAll(IMAGE_SELECTORS.join(', '))) as HTMLElement[];

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'asw-floating-tooltip';
        document.body.appendChild(tooltipEl);
    }

    injectToolCSS({
        ...imageTooltipsConfig,
        enable
    });

    if (enable) {
        images.forEach(img => {
            const title = img.getAttribute('alt') || img.getAttribute('aria-label') || img.querySelector('title')?.textContent || img.getAttribute('title');

            if (title && title.trim()) {
                const onEnter = () => {
                    if (tooltipEl) {
                        tooltipEl.textContent = title;
                        tooltipEl.classList.add('asw-visible');
                    }
                };

                const onMove = (e: MouseEvent) => {
                    if (tooltipEl) {
                        const gap = 20;
                        const windowWidth = window.innerWidth;
                        const windowHeight = window.innerHeight;

                        const rect = tooltipEl.getBoundingClientRect();
                        const tooltipWidth = rect.width;
                        const tooltipHeight = rect.height;

                        let x = e.clientX + gap;
                        let y = e.clientY + gap;

                        // Check right edge
                        if (x + tooltipWidth > windowWidth - gap) {
                            x = e.clientX - tooltipWidth - gap;
                        }

                        // Check left edge
                        if (x < gap) {
                            x = gap;
                        }

                        // Check bottom edge
                        if (y + tooltipHeight > windowHeight - gap) {
                            y = e.clientY - tooltipHeight - gap;
                        }

                        // Check top edge
                        if (y < gap) {
                            y = gap;
                        }

                        tooltipEl.style.left = `${x}px`;
                        tooltipEl.style.top = `${y}px`;
                    }
                };

                const onLeave = () => {
                    if (tooltipEl) {
                        tooltipEl.classList.remove('asw-visible');
                    }
                };

                img.addEventListener('mouseenter', onEnter);
                img.addEventListener('mousemove', onMove);
                img.addEventListener('mouseleave', onLeave);

                activeListeners.set(img, { enter: onEnter, move: onMove, leave: onLeave });
            }
        });
    } else {
        activeListeners.forEach((listeners, img) => {
            img.removeEventListener('mouseenter', listeners.enter);
            img.removeEventListener('mousemove', listeners.move);
            img.removeEventListener('mouseleave', listeners.leave);
        });
        activeListeners.clear();

        if (tooltipEl) {
            tooltipEl.classList.remove('asw-visible');
        }
    }
}
